// Paso ÚNICO del onboarding de Tiendanube: "elegí tu producto y generá tu primera imagen".
// Clon del look del paso 4 del cuestionario (AccountSetupProduct) pero con las opciones adaptadas:
//   1) Subir una foto propia  → generarPrimeraImagen (crea producto + genera, como el alta normal).
//   2) Usar un producto de tu tienda → grid con los primeros productos importados de Tiendanube;
//      al elegir uno se genera con el motor "Automática" (generateClassicImages, sin recrear producto).
// Los dos caminos terminan en la MISMA pantalla de resultados (/redes-sociales/imagenes-generadas).
//
// Salvavidas: el import de productos es asíncrono (n8n) → al montar reintentamos unos segundos;
// si no llegan, mostramos igual la opción de subir foto. No se acopla al wizard de Atlas.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Icon } from '../../design-system/icons';
import { fetchUserProducts } from '../v2/productsApi';
import { generateClassicImages } from '../v2/imageGenerationRecovery';
import { preloadGeneratedImages } from '../../utils/preloadGeneratedImages';
import { GenerateAdError } from '../../lib/generateAdRequest';
import { atlasBackendFetch } from '../../lib/atlasBackend';
import {
  generarPrimeraImagen,
  prepararImagen,
  ImagenInvalidaError,
} from '../../lib/primeraGeneracion';
import type { MockProductV2 } from '../v2/mockData';
import LoadingAnimation from '../../components/LoadingAnimation';
import { LOADING_CAROUSEL_IMAGES } from '../../components/loading/loadingCarousel';

type Option = 'upload' | 'store' | null;

// Cantidad de imágenes de la primera generación (mismo criterio que el alta normal).
const EXPECTED = 2;
// Cuántos productos de la tienda mostramos para elegir (los más recientes con imagen).
const STORE_PRODUCTS = 3;
// Reintentos del fetch de productos mientras el import async va cargando.
const FETCH_RETRIES = 6;
const FETCH_DELAY_MS = 2000;
// Ruta de resultados (la misma que usa la Interfaz V2).
const RESULTADOS = '/redes-sociales/imagenes-generadas';
// Duración estimada de la barra de carga (paceada con tope 97%; salta a 100% al terminar de verdad).
// "rapido" con 2 imágenes ronda los ~30s.
const GEN_DURATION_MS = 40_000;

/** Radio del DS (aro + punto), igual que AccountSetupProduct. */
function RadioDot({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden
      className={`grid size-5 shrink-0 place-items-center rounded-full border border-[#18181B] transition-colors duration-200 ${
        selected ? 'bg-[#18181B]' : ''
      }`}
    >
      <span
        className={`size-1.5 rounded-full bg-white transition-transform duration-200 ${
          selected ? 'scale-100' : 'scale-0'
        }`}
      />
    </span>
  );
}

/** Contenedor colapsable animado por altura (grid-template-rows 0fr↔1fr). */
function Collapse({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      className="grid transition-[grid-template-rows] duration-300 ease-out"
      style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}

export default function TiendanubeProducto() {
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [products, setProducts] = useState<MockProductV2[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const [option, setOption] = useState<Option>(null);
  const [productName, setProductName] = useState('');
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);

  const [generating, setGenerating] = useState(false);
  // Ciclo de la pantalla de carga unificada (igual que el alta): `listo` lleva la barra a 100% cuando
  // la generación terminó de verdad; `saliendo` dispara el fade; al terminar el fade navegamos.
  const [listo, setListo] = useState(false);
  const [saliendo, setSaliendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorArchivo, setErrorArchivo] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrlRef = useRef<string | null>(null);
  const archivoRef = useRef<File | null>(null);
  // Resultado de la generación, para navegar a resultados recién cuando termina el fade de salida.
  const resultadoRef = useRef<{
    imageIds: Array<string | number>;
    companyId: string;
    preloadedImages: unknown;
  } | null>(null);

  // Montaje: resolver la empresa y traer los productos importados (con reintentos, porque el
  // import corre async en n8n y puede no haber terminado cuando el usuario llega acá).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!user) {
        navigate('/tiendanube', { replace: true });
        return;
      }
      const { data: prof } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .maybeSingle();
      const cid = (prof as { id_company?: string | null } | null)?.id_company ?? null;
      if (cancelled) return;
      setCompanyId(cid);
      if (!cid) {
        setLoadingProducts(false);
        return;
      }
      // Fase 3: clasificar el rubro por los productos (best-effort, fire-and-forget). El backend solo
      // lo escribe si el rubro está en placeholder ('Otros') → no pisa uno definido.
      const clasificarRubro = (nombres: string[]) => {
        const limpios = nombres.map((n) => n?.trim()).filter(Boolean);
        if (!limpios.length) return;
        atlasBackendFetch('/v1/tiendanube/classify-rubro', { product_names: limpios }).catch(() => {
          /* si falla, el rubro queda en el placeholder */
        });
      };
      for (let i = 0; i < FETCH_RETRIES && !cancelled; i++) {
        try {
          const prods = await fetchUserProducts(cid);
          const withImg = prods.filter((p) => p.imageUrl).slice(0, STORE_PRODUCTS);
          if (withImg.length > 0) {
            if (!cancelled) {
              setProducts(withImg);
              setLoadingProducts(false);
            }
            clasificarRubro(prods.map((p) => p.name));
            return;
          }
        } catch {
          /* reintentamos */
        }
        await new Promise((r) => setTimeout(r, FETCH_DELAY_MS));
      }
      if (!cancelled) setLoadingProducts(false); // sin productos aún → queda la opción de subir foto
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Liberar el object URL de la foto subida al desmontar.
  useEffect(
    () => () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    [],
  );

  const setUploaded = (url: string | null) => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = url;
    setUploadedImage(url);
  };

  // Se valida/normaliza al elegir el archivo (no al generar).
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setErrorArchivo(null);
    try {
      const preparada = await prepararImagen(file);
      archivoRef.current = preparada.file;
      setUploaded(preparada.previewUrl);
    } catch (err) {
      archivoRef.current = null;
      setUploaded(null);
      setErrorArchivo(
        err instanceof ImagenInvalidaError
          ? err.message
          : 'No pudimos procesar esa imagen. Probá con otra.',
      );
    }
  };

  const canGenerate =
    (option === 'upload' && !!uploadedImage) || (option === 'store' && !!selectedVariantId);

  const handleGenerate = async () => {
    if (!canGenerate || generating) return;
    setError(null);
    setGenerating(true);
    try {
      let imageIds: Array<string | number> = [];
      let cid = companyId ?? '';

      if (option === 'upload') {
        if (!archivoRef.current) {
          setError('Se perdió la foto. Volvé a subirla e intentá de nuevo.');
          setGenerating(false);
          return;
        }
        const res = await generarPrimeraImagen(archivoRef.current, productName);
        imageIds = res.imageIds;
        cid = res.companyId || cid;
      } else {
        // Producto ya importado: solo hace falta el variante_id. Reusa el motor "Automática".
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const payload: Record<string, unknown> = {
          user_id: user?.id, // el backend lo deriva del JWT; se manda para el fallback n8n
          variante_id: [selectedVariantId],
          mode: 'single',
          modo: 'rapido',
          modo_utilizado: 'imagen_automatica',
          cantidad_imagenes: EXPECTED,
          Format: '4:5',
          composicion: 'producto',
          background_mode: 'ia',
          new_ui: true,
          modo_pro: true,
        };
        const res = await generateClassicImages({
          payload,
          companyId: cid,
          expectedCount: EXPECTED,
        });
        imageIds = res.imageIds;
        cid = res.companyId || cid;
      }

      if (!imageIds.length) {
        setError('No se pudo generar la imagen. Probá de nuevo.');
        setGenerating(false);
        return;
      }

      // Precargar para que la pantalla de resultados las muestre al toque (best-effort).
      let preloadedImages: unknown = undefined;
      try {
        preloadedImages = await preloadGeneratedImages(imageIds, cid);
      } catch {
        /* si falla el preload, la pantalla de resultados igual las trae */
      }

      // No navegamos de una: guardamos el resultado y dejamos que la barra llegue a 100% (+ sonido
      // + fade), igual que el onboarding normal. La navegación ocurre en onExitComplete.
      resultadoRef.current = { imageIds, companyId: cid, preloadedImages };
      setListo(true);
    } catch (err) {
      const msg =
        err instanceof GenerateAdError && err.isActionable
          ? err.message
          : 'No se pudo generar la imagen. Probá de nuevo en un momento.';
      setError(msg);
      setGenerating(false);
    }
  };

  // Navega a resultados recién cuando terminó el fade de salida de la pantalla de carga.
  // `isOnboarding: true` → la pantalla de resultados usa el layout de onboarding (igual que el alta
  // normal): SIN botón "Volver" (rompía el flujo) y la salida va a la home del estudio.
  const irAResultados = () => {
    const r = resultadoRef.current;
    navigate(RESULTADOS, {
      replace: true,
      state: { ...(r ?? { imageIds: [], companyId: companyId ?? '' }), isOnboarding: true },
    });
  };

  // Pantalla de generación unificada (misma que el onboarding normal: sparkles + barra + carrusel).
  if (generating) {
    return (
      <LoadingAnimation
        variant="onboarding"
        message="Generando tu primera imagen"
        subMessage="Solo tomará unos segundos"
        carouselTitle="Esto es lo que podés crear con Atlas"
        carouselImages={LOADING_CAROUSEL_IMAGES}
        durationMs={GEN_DURATION_MS}
        completeNow={listo}
        onCompleted={() => setSaliendo(true)}
        exiting={saliendo}
        onExitComplete={irAResultados}
      />
    );
  }

  const cardClass = (active: boolean) =>
    `flex flex-col rounded-[16px] border bg-[#FAFAFA] p-5 transition-[border-color,box-shadow] duration-300 ease-out ${
      active
        ? 'border-[#18181B] shadow-[inset_0_0_0_1px_#18181B]'
        : 'cursor-pointer border-[#D4D4D8] hover:border-[#9F9FA9]'
    }`;

  return (
    <div className="flex min-h-[100dvh] w-full flex-col bg-white">
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />

      {/* Barra de progreso (decorativa, look del cuestionario). */}
      <div className="mx-auto w-full max-w-[800px] px-5 pt-8 md:px-8">
        <div className="h-1.5 w-full rounded-full bg-[#18181B]" />
      </div>

      <div className="mx-auto flex w-full max-w-[800px] flex-1 flex-col gap-10 px-5 pb-40 pt-8 md:px-8">
        <h1 className="text-[24px] font-[700] leading-[30px] text-[#18181B] md:text-center md:text-[28px] md:leading-[34px]">
          Todo listo, elegí tu producto y generá tu primera imagen
        </h1>

        {error && (
          <p role="alert" className="rounded-[12px] bg-[#FEE2E2] px-4 py-3 text-[13px] font-[500] leading-[18px] text-[#991B1B]">
            {error}
          </p>
        )}

        <div className="flex flex-col gap-6" role="radiogroup" aria-label="Elegí cómo cargar tu producto">
          {/* ── Opción 1: usar un producto de tu tienda ── */}
          <div className={cardClass(option === 'store')} onClick={() => setOption('store')}>
            <button
              type="button"
              role="radio"
              aria-checked={option === 'store'}
              onClick={() => setOption('store')}
              className="flex w-full items-start gap-3 rounded-[8px] text-left outline-none focus-visible:ring-2 focus-visible:ring-[#1644EB]/40 focus-visible:ring-offset-2"
            >
              <span className="flex flex-1 flex-col gap-1.5">
                <span className="text-[16px] font-[600] leading-[21px] text-[#18181B]">
                  Usar un producto de tu tienda
                </span>
                <span className="text-[14px] font-[400] leading-[19px] text-[#52525C]">
                  Elegí uno de los productos que trajimos de tu Tiendanube
                </span>
              </span>
              <RadioDot selected={option === 'store'} />
            </button>

            <Collapse open={option === 'store'}>
              <div className="pt-6">
                {loadingProducts ? (
                  <div className="flex items-center gap-2 text-[14px] font-[500] text-[#71717B]">
                    <Loader2 className="size-4 animate-spin" /> Trayendo tus productos…
                  </div>
                ) : products.length === 0 ? (
                  <p className="text-[14px] font-[500] leading-[19px] text-[#71717B]">
                    Todavía estamos trayendo tus productos. Podés subir una foto abajo mientras tanto.
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {products.map((p) => {
                      const sel = selectedVariantId === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setSelectedVariantId(p.id)}
                          className={`flex flex-col items-center gap-2 rounded-[16px] border bg-white p-2 outline-none transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-[#1644EB]/40 ${
                            sel ? 'border-[#18181B] shadow-[inset_0_0_0_1px_#18181B]' : 'border-[#E4E4E7] hover:border-[#D4D4D8]'
                          }`}
                        >
                          <img
                            src={p.imageUrl}
                            alt={p.name}
                            draggable={false}
                            className="aspect-square w-full rounded-[10px] object-cover"
                          />
                          <span className="line-clamp-1 w-full px-1 text-center text-[12px] font-[600] leading-[16px] text-[#18181B]">
                            {p.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </Collapse>
          </div>

          {/* ── Opción 2: subir una foto propia ── */}
          <div className={cardClass(option === 'upload')} onClick={() => setOption('upload')}>
            <button
              type="button"
              role="radio"
              aria-checked={option === 'upload'}
              onClick={() => setOption('upload')}
              className="flex w-full items-start gap-3 rounded-[8px] text-left outline-none focus-visible:ring-2 focus-visible:ring-[#1644EB]/40 focus-visible:ring-offset-2"
            >
              <span className="flex flex-1 flex-col gap-1.5">
                <span className="text-[16px] font-[600] leading-[21px] text-[#18181B]">
                  Subir una foto propia
                </span>
                <span className="text-[14px] font-[400] leading-[19px] text-[#52525C]">
                  Usá una imagen de tu producto
                </span>
              </span>
              <RadioDot selected={option === 'upload'} />
            </button>

            <Collapse open={option === 'upload'}>
              <div className="flex flex-col gap-6 pt-6">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="tn-product-name" className="text-[14px] font-[600] leading-[19px] text-[#18181B]">
                    Nombre del producto
                  </label>
                  <input
                    id="tn-product-name"
                    type="text"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="Ej: Remera básica negra"
                    className="w-full rounded-[12px] border border-[#D4D4D8] bg-white px-4 py-3 text-[16px] font-[400] leading-[22px] text-[#18181B] outline-none placeholder:text-[#71717B] focus:border-[#18181B] md:text-[14px] md:leading-[19px]"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="text-[14px] font-[600] leading-[22px] text-[#18181B]">Foto del producto</span>
                  {uploadedImage ? (
                    <div className="relative size-[120px] overflow-hidden rounded-[12px]">
                      <img src={uploadedImage} alt="Foto del producto subida" className="size-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setUploaded(null)}
                        aria-label="Eliminar foto"
                        className="absolute right-1.5 top-1.5 flex items-center rounded-[8px] bg-white p-1 text-[#18181B] shadow-sm transition-colors hover:bg-[#F4F4F5]"
                      >
                        <Icon name="outline/trash" size={12} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex w-full flex-col items-center justify-center gap-3 rounded-[8px] border-2 border-dashed border-[#3B82F6] bg-white px-4 py-6 outline-none transition-colors hover:bg-[#3B82F6]/5 focus-visible:ring-2 focus-visible:ring-[#3B82F6]/40"
                    >
                      <Icon name="outline/image-plus" size={24} className="text-[#3B82F6]" />
                      <span className="text-[14px] font-[600] leading-[18px] text-[#3B82F6]">Subir foto</span>
                    </button>
                  )}
                  {errorArchivo && (
                    <p role="alert" className="mt-3 rounded-[8px] bg-[#FEE2E2] px-3 py-2 text-[13px] font-[500] leading-[18px] text-[#991B1B]">
                      {errorArchivo}
                    </p>
                  )}
                </div>
              </div>
            </Collapse>
          </div>
        </div>
      </div>

      {/* Footer fijo con "Generar imagen". */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-[#E4E4E7] bg-white px-5 pb-[calc(env(safe-area-inset-bottom)+20px)] pt-4 md:pb-8 md:pt-5">
        <div className="md:mx-auto md:flex md:max-w-[800px] md:justify-end md:px-8">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={!canGenerate}
            className={`relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-[12px] bg-[#9F9FA9] p-4 text-[16px] font-[700] leading-[19px] text-[#FAFAFA] transition-[filter] duration-200 md:w-auto md:px-10 ${
              canGenerate ? 'hover:brightness-110' : 'cursor-not-allowed'
            }`}
          >
            <span
              aria-hidden
              className={`pointer-events-none absolute inset-0 bg-[linear-gradient(181.83deg,#2563F5_8.76%,#0436AE_90.56%)] transition-opacity duration-300 ease-out ${
                canGenerate ? 'opacity-100' : 'opacity-0'
              }`}
            />
            <span className="relative z-10 flex items-center justify-center gap-2">
              <Icon name="solid/sparkles" size={20} />
              Generar imagen
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
